/**
 * Mapowanie wartości słownikowych jednej kartoteki — panel ITE (ADR-0111, faza 27).
 *
 * ── DLACZEGO LISTA POCHODZI Z POBRANYCH DANYCH, A NIE ZE SŁOWNIKA ──
 *
 * Słownik u klienta ma kilkadziesiąt pozycji, z których w kartotece występuje kilka. Ekran
 * pokazujący słownik kazałby mapować w ciemno — wdrożeniowiec nie wiedziałby, które wartości
 * w ogóle padły, a które zostały po dawnych procesach. Dlatego po lewej są wyłącznie wartości,
 * które RZECZYWIŚCIE przyszły z bazy klienta, posortowane po liczbie wystąpień.
 *
 * Skutek uboczny jest celowy: dopóki nie pobrano danych, nie ma czego mapować — i ekran mówi
 * to wprost, zamiast pokazywać pustą listę bez wyjaśnienia.
 *
 * ── TRZY STANY, NIE DWA ──
 *
 * „Przepisz bez zmian" (domyślny), „przełóż na naszą pozycję" i „nie przenoś". Trzeci jest
 * powodem, dla którego ten ekran powstał: w rozpoznanej bazie 131 ze 146 pracowników ma
 * stanowisko „Brak danych" — to wypełniacz, nie stanowisko, i bez możliwości jego pominięcia
 * trafiłby do kartoteki jako kod pozycji, której nie ma w żadnej liście wyboru.
 *
 * Zapisujemy WYŁĄCZNIE wartości dotknięte na ekranie. Zapis całością skasowałby decyzje
 * o wartościach, które tym razem nie wystąpiły w pobranych danych.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

export interface ValueRow {
  value: string;
  count: number;
  sourceLabel: string | null;
  state: 'none' | 'map' | 'skip';
  targetValue: string | null;
}

export interface ValueField {
  sourceField: string;
  sourceLabel: string;
  targetField: string;
  targetLabel: string;
  targetDictionaryCode: string | null;
  targetItems: Array<{ code: string; label: string }>;
  values: ValueRow[];
  truncated: boolean;
}

export interface ValueWorkspace {
  staged: number;
  fields: ValueField[];
}

type ValueCommand =
  | { action: 'map'; sourceField: string; sourceValue: string; targetValue: string }
  | { action: 'skip'; sourceField: string; sourceValue: string }
  | { action: 'clear'; sourceField: string; sourceValue: string };

/**
 * Wartość wybrana na liście. Pusty napis znaczy „przepisz bez zmian", bo to stan domyślny
 * i ma wyglądać na brak decyzji; pominięcie ma własny, jawny znacznik.
 */
const POMIN = '__pomin__';

function wyborDlaWiersza(w: ValueRow): string {
  if (w.state === 'skip') return POMIN;
  if (w.state === 'map') return w.targetValue ?? '';
  return '';
}

/** Klucz wiersza w zmianach roboczych. JSON, bo wartość klienta może zawierać cokolwiek. */
function kluczWiersza(sourceField: string, value: string): string {
  return JSON.stringify([sourceField, value]);
}

export function LegacyValueMappingSection({
  basePath,
  entity,
}: {
  basePath: string;
  entity: 'contractor' | 'employee';
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = [basePath, 'values', entity];

  const [draft, setDraft] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get<ValueWorkspace>(`${basePath}/mappings/${entity}/values`)).data,
  });

  const save = useMutation({
    mutationFn: async (commands: ValueCommand[]) => {
      await api.put(`${basePath}/mappings/${entity}/values`, { commands });
    },
    onSuccess: () => {
      setDraft({});
      toast.show({ variant: 'success', title: t('admin.tenants.legacyMigration.mapping.saved') });
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t('admin.tenants.legacyMigration.mapping.saveFailed'),
        ),
      }),
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data) return null;

  if (data.fields.length === 0) {
    return (
      <Alert tone="info" title={t('admin.tenants.legacyMigration.mapping.values.noFields.title')}>
        {t('admin.tenants.legacyMigration.mapping.values.noFields.body')}
      </Alert>
    );
  }

  if (data.staged === 0) {
    return (
      <Alert tone="info" title={t('admin.tenants.legacyMigration.mapping.values.noData.title')}>
        {t('admin.tenants.legacyMigration.mapping.values.noData.body')}
      </Alert>
    );
  }

  const polecenia: ValueCommand[] = Object.entries(draft).map(([k, wybor]) => {
    const [sourceField, sourceValue] = JSON.parse(k) as [string, string];
    if (wybor === POMIN) return { action: 'skip', sourceField, sourceValue };
    if (wybor === '') return { action: 'clear', sourceField, sourceValue };
    return { action: 'map', sourceField, sourceValue, targetValue: wybor };
  });

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-[var(--text-secondary)]">
        {t('admin.tenants.legacyMigration.mapping.values.intro', { count: data.staged })}
      </p>

      {data.fields.map((pole) => {
        const opcje = [
          { value: '', label: t('admin.tenants.legacyMigration.mapping.values.asIs') },
          { value: POMIN, label: t('admin.tenants.legacyMigration.mapping.values.skip') },
          ...pole.targetItems.map((p) => ({ value: p.code, label: `${p.label} (${p.code})` })),
        ];

        return (
          <div key={pole.sourceField} className="flex flex-col gap-2">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium">{pole.sourceLabel}</span>
              <span className="text-xs text-[var(--text-secondary)]">→ {pole.targetLabel}</span>
              <Badge color="gray">
                {t('admin.tenants.legacyMigration.mapping.values.count', {
                  count: pole.values.length,
                })}
              </Badge>
            </div>

            {pole.values.length === 0 && (
              <span className="text-sm text-[var(--text-secondary)]">
                {t('admin.tenants.legacyMigration.mapping.values.fieldEmpty')}
              </span>
            )}

            {pole.values.map((w) => {
              const k = kluczWiersza(pole.sourceField, w.value);
              return (
                <div key={k} className="flex items-center gap-3">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm">{w.value}</span>
                    <span className="text-xs text-[var(--text-secondary)]">
                      {[
                        w.sourceLabel,
                        t('admin.tenants.legacyMigration.mapping.values.occurrences', {
                          count: w.count,
                        }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  <Select
                    className="w-72"
                    options={opcje}
                    aria-label={`${pole.sourceLabel}: ${w.value}`}
                    value={draft[k] ?? wyborDlaWiersza(w)}
                    onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                  />
                </div>
              );
            })}

            {pole.truncated && (
              <Alert tone="warning">
                {t('admin.tenants.legacyMigration.mapping.values.truncated')}
              </Alert>
            )}
          </div>
        );
      })}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => setDraft({})} disabled={polecenia.length === 0}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => save.mutate(polecenia)}
          disabled={polecenia.length === 0 || save.isPending}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
